import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import styles from './WillLine.module.css'

/**
 * The will at the threshold.
 *
 * The player authors the will, never the performance — plain imperatives at any
 * grain ("say hello" · "greet her warmly" · "find out what's behind the table").
 * The world flows without input, so this is never a turn prompt and is never
 * required; it stays live even while the engine is working.
 */
export default function WillLine({
  affordances,
  disabled,
  onSubmit,
}: {
  affordances: string[]
  disabled?: boolean
  onSubmit: (text: string) => void
}) {
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  function send(ev?: FormEvent) {
    ev?.preventDefault()
    const value = text.trim()
    if (!value || disabled) return
    onSubmit(value)
    setText('')
  }

  function onKeyDown(ev: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter breaks the line. Ctrl/Cmd+Enter also sends, for
    // anyone carrying the habit over from the old surface.
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault()
      send()
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        {affordances.length > 0 && (
          <div className={styles.field}>
            {affordances.map((a) => (
              <button
                key={a}
                type="button"
                className={styles.affordance}
                // Fills the will line rather than acting: the option is a
                // prompt to the player's own intent, and stays amendable.
                onClick={() => {
                  setText(a)
                  inputRef.current?.focus()
                }}
              >
                {a}
              </button>
            ))}
          </div>
        )}

        <form className={styles.form} onSubmit={send}>
          <textarea
            ref={inputRef}
            className={styles.input}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="what do you do?"
            aria-label="What do you do?"
            rows={1}
            disabled={disabled}
          />
          <button
            type="submit"
            className={styles.send}
            disabled={disabled || !text.trim()}
          >
            SEND
          </button>
        </form>
      </div>
    </div>
  )
}
